import type { Email } from "@stockline/shared/types/communications/email"
import { EMAIL_TABLE } from "../../../lib/offline/resources/communications/email/db"
import { useAISync } from "../../ai/providers/AISyncProvider"

export interface UseEmailsFilter {
  emailAccountId?: string
  archived?: boolean
  read?: boolean
  orderBy?: string
  limit?: number
}

/**
 * Most-recent emails to hold in the reactive list. Bounds the Dexie read so it
 * goes through the `emailDate` index cursor instead of a full `getAll()` —
 * Firefox throws `UnknownError` on `getAll()` over a large emails store, which
 * crashed the recap chat panel on open. A chat list never scrolls past the most
 * recent N, so this is a list cap, not a data cap (the recap sibling-resolution
 * read in `offlineShim` reads the full table on its own path).
 */
const EMAIL_LIST_CAP = 1000

/**
 * Reactive list of emails from the Dexie cache. Filters map directly to
 * indexed fields on the {@link EMAIL_TABLE} table.
 */
export function useEmails(filter?: UseEmailsFilter): Email[] {
  const { reactive } = useAISync()
  return reactive.useQuery<Email>(EMAIL_TABLE, {
    ...filter,
    orderBy: filter?.orderBy ?? "-emailDate",
    limit: filter?.limit ?? EMAIL_LIST_CAP,
  })
}

/**
 * Reactive single-email lookup by id. Returns null until the row is in the
 * local cache (initial pull or SSE event).
 */
export function useEmail(id: string | null): Email | null {
  const { reactive } = useAISync()
  return reactive.useOne<Email>(EMAIL_TABLE, id ?? "__none__")
}
